import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/Sidebar';
import Header from '../../components/Header';
import { supportTicketService } from '../../services/supportTicketService';
import { MessageSquare, AlertCircle, Clock, CheckCircle, XCircle, Send, ArrowLeft } from 'lucide-react';

const SupportTickets = () => {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  
  // Form state
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [urgency, setUrgency] = useState('medium');
  const [submitting, setSubmitting] = useState(false);
  const [messageSubscription, setMessageSubscription] = useState(null);

  // Message thread state
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);

  useEffect(() => {
    loadTickets();
  }, []);

  // Real-time subscription for messages when viewing a ticket
  useEffect(() => {
    if (selectedTicket?.id) {
      // Subscribe to new messages
      const subscription = supportTicketService?.subscribeToTicketMessages(
        selectedTicket?.id,
        (newMessage) => {
          console.log('[SupportTickets] Received new message:', newMessage);
          setMessages((prev) => [...prev, newMessage]);
          
          // Auto-scroll to bottom when new message arrives
          setTimeout(() => {
            const messagesContainer = document.getElementById('messages-container');
            if (messagesContainer) {
              messagesContainer.scrollTop = messagesContainer?.scrollHeight;
            }
          }, 100);
        }
      );
      
      setMessageSubscription(subscription);
      
      // Cleanup function
      return () => {
        if (messageSubscription) {
          supportTicketService?.unsubscribeFromTicketMessages(messageSubscription);
        }
      };
    }
  }, [selectedTicket?.id]);

  const loadTickets = async () => {
    try {
      setLoading(true);
      setError(''); // Clear any previous errors
      const data = await supportTicketService?.getUserTickets();
      setTickets(data || []); // Ensure we always set an array
      console.log('[SupportTickets] Tickets loaded successfully:', data?.length || 0);
    } catch (err) {
      console.error('[SupportTickets] Error loading tickets:', err);
      setError(err?.message || 'Failed to load tickets');
      setTickets([]); // Set empty array on error
    } finally {
      setLoading(false); // Always stop loading
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!subject?.trim() || !description?.trim()) {
      setError('Please fill in all fields');
      return;
    }

    try {
      setSubmitting(true);
      setError('');
      await supportTicketService?.createTicket(subject, description, urgency);
      
      // Reset form
      setSubject('');
      setDescription('');
      setUrgency('medium');
      setShowCreateForm(false);
      
      // Reload tickets
      await loadTickets();
    } catch (err) {
      setError(err?.message || 'Failed to create ticket');
    } finally {
      setSubmitting(false);
    }
  };

  const handleTicketClick = async (ticket) => {
    setSelectedTicket(ticket);
    setNewMessage('');
    
    // Mark ticket as viewed to clear notification
    try {
      await supportTicketService?.markTicketAsViewed(ticket?.id);
      console.log('[SupportTickets] Ticket marked as viewed');
    } catch (error) {
      console.error('[SupportTickets] Error marking ticket as viewed:', error);
      // Don't block ticket opening if marking fails
    }
    
    await loadMessages(ticket?.id);
  };

  const loadMessages = async (ticketId) => {
    try {
      setLoadingMessages(true);
      const data = await supportTicketService?.getTicketMessages(ticketId);
      setMessages(data);
    } catch (err) {
      setError(err?.message || 'Failed to load messages');
    } finally {
      setLoadingMessages(false);
    }
  };

  const handleSendMessage = async (e) => {
    e?.preventDefault();
    
    if (!newMessage?.trim()) {
      return;
    }

    try {
      setSendingMessage(true);
      await supportTicketService?.createTicketMessage(selectedTicket?.id, newMessage);
      setNewMessage('');
      // Note: Real-time subscription will handle adding the new message to the list
    } catch (err) {
      setError(err?.message || 'Failed to send message');
      // If real-time fails, manually reload messages
      await loadMessages(selectedTicket?.id);
    } finally {
      setSendingMessage(false);
    }
  };

  const handleBackToList = () => {
    setSelectedTicket(null);
    setMessages([]);
    setNewMessage('');
  };

  const getUrgencyColor = (urgency) => {
    switch (urgency) {
      case 'critical': return 'bg-red-100 text-red-800';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'open': return <Clock className="w-4 h-4 text-blue-500" />;
      case 'in_progress': return <AlertCircle className="w-4 h-4 text-yellow-500" />;
      case 'closed': return <CheckCircle className="w-4 h-4 text-green-500" />;
      default: return <MessageSquare className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'open': return 'Open';
      case 'in_progress': return 'In Progress';
      case 'closed': return 'Closed';
      default: return status;
    }
  };

  // Render message thread view
  if (selectedTicket) {
    return (
      <div className="flex h-screen bg-gray-50">
        <Sidebar />
        <div className="flex-1 flex flex-col ml-64">
          <Header />
          
          <main className="flex-1 overflow-auto p-8">
            {/* Back button and ticket header */}
            <button
              onClick={handleBackToList}
              className="mb-6 flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium"
            >
              <ArrowLeft className="w-5 h-5" />
              Back to Tickets
            </button>

            <div className="mb-6 bg-white rounded-lg shadow-md border border-gray-200 p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    {getStatusIcon(selectedTicket?.status)}
                    <h2 className="text-2xl font-bold text-gray-900">{selectedTicket?.subject}</h2>
                  </div>
                  <p className="text-gray-600 mb-4">{selectedTicket?.description}</p>
                  <div className="flex items-center gap-4 text-sm">
                    <span className={`px-3 py-1 rounded-full font-medium ${getUrgencyColor(selectedTicket?.urgency)}`}>
                      {selectedTicket?.urgency?.charAt(0)?.toUpperCase() + selectedTicket?.urgency?.slice(1)}
                    </span>
                    <span className="text-gray-500">
                      Status: {getStatusText(selectedTicket?.status)}
                    </span>
                    <span className="text-gray-400">
                      Created: {new Date(selectedTicket?.createdAt)?.toLocaleDateString()}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Messages Thread */}
            <div className="bg-white rounded-lg shadow-md border border-gray-200 flex flex-col" style={{ height: 'calc(100vh - 450px)' }}>
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-xl font-semibold text-gray-900">Conversation</h3>
              </div>

              {/* Messages list */}
              <div 
                id="messages-container"
                className="flex-1 overflow-auto p-6 space-y-4"
              >
                {loadingMessages ? (
                  <div className="text-center py-8">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    <p className="mt-2 text-gray-600">Loading messages...</p>
                  </div>
                ) : messages?.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <MessageSquare className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                    <p>No messages yet. Start the conversation!</p>
                  </div>
                ) : (
                  messages?.map((msg) => (
                    <div
                      key={msg?.id}
                      className={`flex ${msg?.isAdmin ? 'justify-start' : 'justify-end'}`}
                    >
                      <div className={`max-w-2xl rounded-lg p-4 ${
                        msg?.isAdmin 
                          ? 'bg-blue-50 border border-blue-200' :'bg-gray-100 border border-gray-200'
                      }`}>
                        <div className="flex items-center gap-2 mb-2">
                          <span className="font-semibold text-sm text-gray-900">
                            {msg?.senderName}
                          </span>
                          {msg?.isAdmin && (
                            <span className="px-2 py-0.5 bg-blue-600 text-white text-xs rounded-full">
                              Admin
                            </span>
                          )}
                          <span className="text-xs text-gray-500">
                            {new Date(msg?.createdAt)?.toLocaleString()}
                          </span>
                        </div>
                        <p className="text-gray-700">{msg?.message}</p>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Message input */}
              <div className="p-4 border-t border-gray-200">
                <form onSubmit={handleSendMessage} className="flex gap-3">
                  <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e?.target?.value)}
                    placeholder="Type your message..."
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    disabled={sendingMessage || selectedTicket?.status === 'closed'}
                  />
                  <button
                    type="submit"
                    disabled={sendingMessage || !newMessage?.trim() || selectedTicket?.status === 'closed'}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    <Send className="w-4 h-4" />
                    {sendingMessage ? 'Sending...' : 'Send'}
                  </button>
                </form>
                {selectedTicket?.status === 'closed' && (
                  <p className="text-sm text-gray-500 mt-2">This ticket is closed. Messages cannot be sent.</p>
                )}
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 flex flex-col ml-64">
        <Header />
        
        <main className="flex-1 overflow-auto p-8">
          {/* Header Section */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Support Tickets</h1>
            <p className="text-gray-600">Report bugs or request support</p>
          </div>

          {/* Error Display */}
          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
              <XCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
              <p className="text-red-800">{error}</p>
            </div>
          )}

          {/* Create Ticket Button */}
          {!showCreateForm && (
            <button
              onClick={() => setShowCreateForm(true)}
              className="mb-6 bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center gap-2"
            >
              <Send className="w-5 h-5" />
              Report a Bug / Request Support
            </button>
          )}

          {/* Create Ticket Form */}
          {showCreateForm && (
            <div className="mb-8 bg-white rounded-lg shadow-md border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900">Create New Ticket</h2>
                <button
                  onClick={() => {
                    setShowCreateForm(false);
                    setError('');
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Subject *
                  </label>
                  <input
                    type="text"
                    value={subject}
                    onChange={(e) => setSubject(e?.target?.value)}
                    placeholder="Brief description of the issue"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description *
                  </label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e?.target?.value)}
                    placeholder="Explain the issue in detail..."
                    rows={5}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Urgency *
                  </label>
                  <select
                    value={urgency}
                    onChange={(e) => setUrgency(e?.target?.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="low">Low - Minor issue</option>
                    <option value="medium">Medium - Normal issue</option>
                    <option value="high">High - Important issue</option>
                    <option value="critical">Critical - Urgent issue</option>
                  </select>
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="flex-1 bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    {submitting ? 'Submitting...' : 'Submit Ticket'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateForm(false);
                      setError('');
                    }}
                    className="px-6 py-3 border border-gray-300 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Tickets List */}
          <div className="bg-white rounded-lg shadow-md border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">My Tickets</h2>
            </div>

            {loading ? (
              <div className="p-8 text-center">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <p className="mt-2 text-gray-600">Loading tickets...</p>
              </div>
            ) : tickets?.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <MessageSquare className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                <p>No tickets yet. Create one to get started.</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {tickets?.map((ticket) => (
                  <div 
                    key={ticket?.id} 
                    onClick={() => handleTicketClick(ticket)}
                    className="p-6 hover:bg-gray-50 transition-colors cursor-pointer"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          {getStatusIcon(ticket?.status)}
                          <h3 className="text-lg font-semibold text-gray-900">{ticket?.subject}</h3>
                          <MessageSquare className="w-4 h-4 text-blue-500" />
                        </div>
                        <p className="text-gray-600 text-sm">{ticket?.description}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-4 text-sm">
                      <span className={`px-3 py-1 rounded-full font-medium ${getUrgencyColor(ticket?.urgency)}`}>
                        {ticket?.urgency?.charAt(0)?.toUpperCase() + ticket?.urgency?.slice(1)}
                      </span>
                      <span className="text-gray-500">
                        Status: {getStatusText(ticket?.status)}
                      </span>
                      <span className="text-gray-400">
                        Created: {new Date(ticket?.createdAt)?.toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default SupportTickets;